import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { userAPI } from '../../lib/api';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { chartTheme, tooltipStyle } from '../../lib/chartTheme';
import { useTranslation } from 'react-i18next';
import {
  BookOpen, Target, TrendingUp, Brain, ArrowRight, ChevronLeft,
  Lightbulb, Sparkles, BarChart3, Play
} from 'lucide-react';

export function LearningPathPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await userAPI.getDashboard();
        setDashboard(res.data);
      } catch (error) {
        console.error('Failed to load learning data:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [i18n.language]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 space-y-6 py-6">
        <Skeleton className="h-10 w-64 bg-slate-800/50" />
        <div className="space-y-4">
          {[1,2,3].map(i => <Skeleton key={i} className="h-32 rounded-2xl bg-slate-800/50" />)}
        </div>
      </div>
    );
  }

  const topicPerformance = dashboard?.topic_performance || [];
  const recommendations = dashboard?.recommendations || [];
  const weaknesses = dashboard?.weaknesses || [];

  const chartData = topicPerformance.slice(0, 8).map(t => ({
    name: t.topic.length > 12 ? t.topic.slice(0, 12) + '...' : t.topic,
    fullName: t.topic,
    accuracy: t.accuracy,
    questions: t.total_questions
  }));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 space-y-6 pb-8" data-testid="learning-path-page">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-4 pt-2"
      >
        <Button onClick={() => navigate('/dashboard')} variant="ghost" size="sm" className="text-slate-400">
          <ChevronLeft className="w-4 h-4 mr-1" /> {t('common.back', 'Back')}
        </Button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-white font-['Space_Grotesk']"
              data-testid="learning-path-title">{t('learning.title', 'Personalized Learning Path')}</h1>
          <p className="text-sm text-slate-400 mt-0.5">{t('learning.subtitle', 'Focus areas based on your assessment performance')}</p>
        </div>
      </motion.div>

      {/* Topic Performance Chart */}
      {chartData.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4 sm:p-6" data-testid="learning-path-progress-trend">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-white">{t('learning.skillDomainPerf', 'Skill Domain Performance')}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{t('learning.accuracyByTopic', 'Accuracy by topic area')}</p>
              </div>
              <Badge variant="outline" className="text-teal-300 border-teal-500/30 bg-teal-500/10">
                <BarChart3 className="w-3 h-3 mr-1" /> {t('learning.topics', 'Topics')}
              </Badge>
            </div>
            <div className="h-[260px] w-full">
              <ResponsiveContainer>
                <BarChart data={chartData} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} horizontal={false} />
                  <XAxis type="number" domain={[0, 100]} tick={{ fill: chartTheme.text, fontSize: 11 }} tickFormatter={v => `${v}%`} />
                  <YAxis dataKey="name" type="category" width={100} tick={{ fill: chartTheme.text, fontSize: 11 }} />
                  <Tooltip {...tooltipStyle} formatter={(v, name, props) => [`${v}% (${props.payload.questions} ${t('learning.questions', 'questions')})`, props.payload.fullName]} />
                  <Bar dataKey="accuracy" radius={[0, 6, 6, 0]} maxBarSize={28}>
                    {chartData.map((entry, i) => (
                      <Cell key={i} fill={entry.accuracy >= 80 ? chartTheme.seafoam : entry.accuracy >= 50 ? chartTheme.amber : chartTheme.rose} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </motion.div>
      )}

      {/* Recommendations */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <Card className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4 sm:p-6" data-testid="learning-path-recommendations">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="w-5 h-5 text-amber-400" />
            <h3 className="text-sm font-semibold text-white">{t('learning.recommendedFocus', 'Recommended Focus Areas')}</h3>
          </div>
          
          {recommendations.length > 0 ? (
            <div className="space-y-3">
              {recommendations.map((rec, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  className="p-4 rounded-xl bg-slate-800/40 border border-slate-700/30 hover:border-slate-600/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Target className={`w-4 h-4 ${rec.priority === 'high' ? 'text-rose-400' : 'text-amber-400'}`} />
                        <span className="text-sm font-semibold text-white">{rec.topic}</span>
                        <Badge variant="outline" className={`text-xs ${rec.priority === 'high' ? 'text-rose-300 border-rose-500/30 bg-rose-500/10' : 'text-amber-300 border-amber-500/30 bg-amber-500/10'}`}>
                          {rec.priority === 'high' ? t('learning.highPriority', 'High Priority') : t('learning.mediumPriority', 'Medium Priority')}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-400 mb-2">{rec.suggestion}</p>
                      <div className="flex items-center gap-2">
                        <Progress value={rec.accuracy} className="h-1.5 flex-1 bg-slate-700" />
                        <span className="text-xs font-['Azeret_Mono'] text-slate-400">{rec.accuracy}%</span>
                      </div>
                    </div>
                    <Button size="sm"
                      onClick={() => navigate(rec.topic_id ? `/practice?topic_id=${rec.topic_id}` : '/practice')}
                      className="shrink-0 bg-teal-500/15 text-teal-300 hover:bg-teal-500/25 border border-teal-500/25 rounded-xl h-8 text-xs"
                      data-testid={`practice-btn-${i}`}>
                      <Play className="w-3 h-3 mr-1.5" /> {t('learning.practice', 'Practice')}
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Sparkles className="w-12 h-12 text-teal-500/30 mx-auto mb-3" />
              <p className="text-slate-400 font-medium">{t('learning.excellentWork', 'Excellent work!')}</p>
              <p className="text-sm text-slate-500 mt-1">{t('learning.allAbove80', 'All your skill domains are above 80%. Keep it up!')}</p>
              <Button onClick={() => navigate('/practice')}
                className="mt-4 bg-teal-500/15 text-teal-300 hover:bg-teal-500/25 border border-teal-500/25 rounded-xl">
                <Play className="w-4 h-4 mr-2" /> {t('learning.freePractice', 'Free Practice')}
              </Button>
            </div>
          )}
        </Card>
      </motion.div>

      {/* Why This Matters */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
        <Card className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4 sm:p-6" data-testid="learning-path-why-panel">
          <div className="flex items-center gap-2 mb-3">
            <Brain className="w-5 h-5 text-teal-400" />
            <h3 className="text-sm font-semibold text-white">{t('learning.whyTitle', 'Why Personalized Learning?')}</h3>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed">
            {t('learning.whyBody', 'SocraQuest analyzes your assessment performance across all skill domains to identify patterns. By focusing on areas where you score below 80%, you can improve overall mastery efficiently. Topics are ranked by priority — high priority means accuracy below 50%, requiring immediate attention.')}
          </p>
          <Button
            onClick={() => navigate('/home')}
            className="mt-4 bg-teal-500 text-slate-950 hover:bg-teal-400 rounded-xl"
          >
            <Play className="w-4 h-4 mr-2" /> {t('learning.startPractice', 'Start Practice Assessment')}
          </Button>
        </Card>
      </motion.div>
    </div>
  );
}
